import { useState, useEffect } from "react";
import CheckoutContext from "./checkout-context";
import {
  getGtmProductObject,
  getGtmTotalsObject,
} from "../../assets/js/gtm-helpers";
import {
  getCurrentStepEnum,
  isStepModuleActive,
  isStepPersonalActive,
  isStepSummaryActive,
  StepEnum,
} from "./utils/stepEnums";
import { isCheckoutOffer, isCheckoutRegular } from "./utils/checkoutType";
import { scrollToTop } from "../../assets/js/scrollToTop";
import { getTextsMapping } from "./utils/getTextsMapping";
import { RegularCheckout } from "./regularCheckout/RegularCheckout";
import { OfferCheckout } from "./offerCheckout/OfferCheckout";
import { fetchAddressDetails } from "./utils/fetchAddressDetails";

export default function Checkout({
  checkoutEndPointUrl,
  backToCartLink,
  courseEndPointUrl,
  customerDataEndpointUrl,
  addressEndpointUrl,
  cocEndpointUrl,
  checkoutData: initialCheckoutData,
  resourcedTexts,
  initialCurrentStep,
}) {
  const [state, setState] = useState(() => {
    return {
      checkoutData: JSON.parse(JSON.stringify(initialCheckoutData)), // Creates a deep copy to avoid changes in the object
      cart: initialCheckoutData.cart,
      relatedItems: initialCheckoutData.relatedItems,
      customerData: initialCheckoutData.customerData,
      customerDataValid: true,
      moduleScheduleDataValid: true,
      currentStep: initialCurrentStep || 0, // initialCurrentStep is a prop, used for demo purposes on static
      lastStep: 1,
      serverErrors: initialCheckoutData.errorMessages,
      errors: {},
      accountAgreement: initialCheckoutData.customerData.isLoggedIn,
      accountPrivacyAgreement: initialCheckoutData.customerData.isLoggedIn,
      invalidFormMessage: false,
      checkDiscountActive: false,
      consentData: initialCheckoutData.consents.map((consentItem) => {
        consentItem.isChecked = false;
        return consentItem;
      }),
      isOrderButtonLoading: false,
      customerAddressNotFoundErrorMessage: "",
      companyAddressNotFoundErrorMessage: "",
      textsMapping: getTextsMapping(
        resourcedTexts,
        initialCheckoutData.checkoutType
      ),
      invalidModuleItems: [],
    };
  });

  useEffect(() => {
    const { cart } = state;

    if (cart.lineItems.length) {
      setState({
        ...state,
        lastStep: checkHasModuleScheduler() ? 2 : 1,
        checkDiscountActive: isDiscountActive(),
      });
    }

    handleGtmStepEvent();
  }, []);

  useEffect(() => {
    handleGtmStepEvent();
    scrollToTop();
  }, [state.currentStep]);

  function handleGtmStepEvent() {
    const { currentStep, cart, checkoutData } = state;
    const currentStepEnum = getCurrentStepEnum(
      currentStep,
      checkHasModuleScheduler(),
      checkoutData.checkoutType
    );

    window.dataLayer = window.dataLayer || [];

    switch (currentStepEnum) {
      case StepEnum.MODULE:
        /**
         * Nevi regards the Cart visit as the first checkout step (GTM event checkoutStep1), hence
         * checkoutStep2 is used for what we technically would call checkout step 1.
         **/
        window.dataLayer.push({
          event: "checkoutStep2",
          ecommerce: {
            checkout: {
              products: cart.lineItems.map((product) =>
                getGtmProductObject(product)
              ),
              actionField: {
                ...getGtmTotalsObject(cart),
                step: 2,
              },
            },
          },
        });
        break;
      case StepEnum.PERSONAL:
        window.dataLayer.push({
          event: "checkoutStep3",
          ecommerce: {
            checkout: {
              products: cart.lineItems.map((product) =>
                getGtmProductObject(product, true)
              ),
              actionField: {
                ...getGtmTotalsObject(cart),
                step: 3,
              },
            },
          },
        });
        break;
      case StepEnum.SUMMARY:
        window.dataLayer.push({
          event: "checkoutStep4",
          ecommerce: {
            checkout: {
              products: cart.lineItems.map((product) =>
                getGtmProductObject(product, true)
              ),
              actionField: {
                ...getGtmTotalsObject(cart),
                step: 4,
              },
            },
          },
        });
        break;
      case StepEnum.QUOTE_PERSONAL:
        window.dataLayer.push({
          event: "quoteStep1",
          ecommerce: {
            checkout: {
              products: cart.lineItems.map((product) =>
                getGtmProductObject(product)
              ),
              actionField: {
                step: 1,
                option: "offerte_aanvraag",
              },
            },
          },
        });
        break;
      case StepEnum.QUOTE_SUMMARY:
        window.dataLayer.push({
          event: "quoteStep2",
          ecommerce: {
            checkout: {
              products: cart.lineItems.map((product) =>
                getGtmProductObject(product)
              ),
              actionField: {
                step: 2,
                option: "offerte_aanvraag",
              },
            },
          },
        });
    }
  }

  function checkHasModuleScheduler() {
    const { cart, checkoutData } = state;

    if (isCheckoutOffer(checkoutData.checkoutType)) {
      return false;
    }

    let hasModuleScheduler = false;
    [].forEach.call(cart.lineItems, (cartItem) => {
      if (cartItem.withPlanner) {
        hasModuleScheduler = true;
      }
    });
    return hasModuleScheduler;
  }

  function isDiscountActive() {
    const { checkoutData, cart } = state;

    if (checkoutData.isMember) {
      return true;
    }

    const isDiscountActive = cart.lineItems.some(
      (cartItem) =>
        checkoutData.membershipItem &&
        cartItem.sku === checkoutData.membershipItem.sku
    );

    return isDiscountActive;
  }

  function getInvalidModuleItems() {
    const { lineItems } = state.cart;

    const lineItemsWithPlanner = lineItems.filter(
      (lineItem) => lineItem.withPlanner
    );

    const newInvalidModuleItems = [];
    lineItemsWithPlanner.forEach((lineItem) => {
      if (!lineItem.selectedCourseId) {
        newInvalidModuleItems.push(lineItem.sku);
      }
    });

    return newInvalidModuleItems;
  }

  function handleCourseClick(e, sku, courseId) {
    e.preventDefault();
    handleSelectedCourseReq(sku, courseId);

    const hasInvalidModuleItems = state.invalidModuleItems.length > 0;
    if (hasInvalidModuleItems) {
      setState({ ...state, invalidModuleItems: getInvalidModuleItems() });
    }
  }

  function handleSelectedCourseReq(sku, courseId) {
    /** To get direct feedback we already update the state, before the XHR call is done. This makes it also work on the static environment: **/
    const newCart = { ...state.cart };
    newCart.lineItems = newCart.lineItems.map((item) => {
      if (item.sku === sku) {
        item.selectedCourseId = courseId;
        return item;
      }
      return item;
    });

    setState({
      ...state,
      checkoutData: {
        ...state.checkoutData,
        cart: newCart,
      },
      cart: newCart,
    });

    fetch(courseEndPointUrl, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        sku: sku,
        selectedCourseId: courseId,
      }),
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        }
      })
      .then((json) => {
        if (!json.success) {
          setState({ ...state, serverErrors: json.errorMessages });

          return;
        }

        setState({
          ...state,
          serverErrors: [],
          checkoutData: {
            ...state.checkoutData,
            cart: json.payload.cart,
          },
          cart: json.payload.cart,
          relatedItems: json.payload.relatedItems,
        });
      })
      .catch((err) => err);
  }

  function handleCustomerData() {
    const { checkoutData, currentStep } = state;

    fetch(customerDataEndpointUrl, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(checkoutData.customerData),
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        }
      })
      .then((json) => {
        if (!json.success) {
          setState({ ...state, serverErrors: json.errorMessages });

          window.scrollTo(0, 0);

          return;
        }

        setState({
          ...state,
          serverErrors: [],
          customerData: json.payload,
          currentStep: currentStep + 1,
        });
      })
      .catch((err) => err);
  }

  function handleOrderConfirm() {
    const { consentData } = state;
    const allConsentsAreTrue = consentData.every((item) => item.isChecked);

    if (allConsentsAreTrue) {
      setState({ ...state, isOrderButtonLoading: true });
      handleOrderConfirmReq();
    } else {
      setState({
        ...state,
        invalidFormMessage: true,
        customerDataValid: false,
      });
    }
  }

  function handleOrderConfirmReq() {
    const { checkoutData } = state;

    fetch(checkoutEndPointUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        RequestVerificationToken: checkoutData.requestVerificationToken,
      },
      body: JSON.stringify({
        customerData: checkoutData.customerData,
        consents: checkoutData.consents,
        checkoutType: checkoutData.checkoutType,
        selectedPaymentMethod: checkoutData.selectedPaymentMethod,
      }),
    })
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          return response.json();
        }
      })
      .then((json) => {
        if (!json.success) {
          setState({
            ...state,
            serverErrors: json.errorMessages,
            isOrderButtonLoading: false,
          });

          window.scrollTo(0, 0);
          return;
        }

        removeServerErrors();

        window.location.href = json.payload.thankYouUrl;
      })
      .catch((err) => err);
  }

  function changeAccountAgreement() {
    setState({ ...state, accountAgreement: !state.accountAgreement });
  }

  function changeAccountPrivacyAgreement() {
    setState({
      ...state,
      accountPrivacyAgreement: !state.accountPrivacyAgreement,
    });
  }

  function changeConsentAgreements(consentCode) {
    setState({
      ...state,
      consentData: state.consentData.map((item) => {
        if (item.consentCode === consentCode) {
          item.isChecked = !item.isChecked;
        }
        return item;
      }),
    });
  }

  function trimInput(key) {
    const value = getData(key);
    const trimmedValue = value.trim();
    setData(key, trimmedValue);
  }

  function getPersonalAddressDetails() {
    getAddressDetails(false);
  }

  function getCompanyAddressDetails() {
    getAddressDetails(true);
  }

  function getAddressDetails(isCompanyRequest = false) {
    const { textsMapping, checkoutData, errors } = state;

    let address;

    if (isCompanyRequest) {
      address =
        checkoutData.customerData.companyInvoiceDetails.selectedCompany.address;
    } else {
      address = checkoutData.customerData.address;
    }

    let zipcode = address.zip;
    let housenumber = address.houseNumber;
    let defaultCountry = address.defaultCountry;

    if (!zipcode || !housenumber || !defaultCountry) {
      return;
    }

    fetchAddressDetails(addressEndpointUrl, zipcode, housenumber).then(
      (json) => {
        const isValid = Boolean(json.streetName && json.city);
        const errorMessage = isValid ? "" : textsMapping.addressNotFoundError;

        address.street = json.streetName ? json.streetName : "";
        address.city = json.city ? json.city : "";

        let streetKey, cityKey;
        if (isCompanyRequest) {
          streetKey =
            "customerData.companyInvoiceDetails.selectedCompany.address.street";
          cityKey =
            "customerData.companyInvoiceDetails.selectedCompany.address.city";
        } else {
          streetKey = "customerData.address.street";
          cityKey = "customerData.address.city";
        }

        if (isValid) {
          delete errors[streetKey];
          delete errors[cityKey];
        } else {
          const inputErrorMessage = textsMapping.inputErrorMessageDefault;
          errors[streetKey] = inputErrorMessage;
          errors[cityKey] = inputErrorMessage;
        }

        if (isCompanyRequest) {
          setState({
            ...state,
            checkoutData,
            errors,
            companyAddressNotFoundErrorMessage: errorMessage,
          });
        } else {
          setState({
            ...state,
            checkoutData,
            errors,
            customerAddressNotFoundErrorMessage: errorMessage,
          });
        }
      }
    );
  }

  function defaultCountryRadioButtonChanged(key) {
    const isNotRequiredValue = false;

    if (
      key.indexOf(
        "customerData.companyInvoiceDetails.selectedCompany.address"
      ) < 0
    ) {
      clearField("customerData.address.zip");
      clearField("customerData.address.houseNumber");
      clearField(
        "customerData.address.houseNumberAddition",
        isNotRequiredValue
      ); // Required is false for this field.
      clearField("customerData.address.street");
      clearField("customerData.address.city");
    } else {
      clearField(
        "customerData.companyInvoiceDetails.selectedCompany.address.zip"
      );
      clearField(
        "customerData.companyInvoiceDetails.selectedCompany.address.houseNumber"
      );
      clearField(
        "customerData.companyInvoiceDetails.selectedCompany.address.houseNumberAddition",
        isNotRequiredValue
      ); // Required is false for this field.
      clearField(
        "customerData.companyInvoiceDetails.selectedCompany.address.street"
      );
      clearField(
        "customerData.companyInvoiceDetails.selectedCompany.address.city"
      );
    }

    setState({
      ...state,
      customerAddressNotFoundErrorMessage: "",
      companyAddressNotFoundErrorMessage: "",
    });
  }

  function isAllModuleScheduleDataValid() {
    const { lineItems } = state.cart;
    let moduleScheduleDataIsValid = true;

    [].forEach.call(lineItems, (cartItem) => {
      if (
        (cartItem.withPlanner &&
          moduleScheduleDataIsValid &&
          cartItem.selectedCourseId === "") ||
        cartItem.selectedCourseId === null
      ) {
        moduleScheduleDataIsValid = false;
      }
    });

    return moduleScheduleDataIsValid;
  }

  function getData(key) {
    const { checkoutData } = state;

    const keyParts = key.split(".");

    let data = checkoutData;
    for (const keyPart of keyParts) {
      data = data[keyPart];

      if (data == null) return "";
    }

    return data == null ? "" : data;
  }

  function setData(key, value) {
    const { checkoutData } = state;
    var object = checkoutData;

    const keyParts = key.split(".");

    var length = keyParts.length;
    for (var index = 0; index < length - 1; index++) {
      var element = keyParts[index];
      if (!object[element]) object[element] = {};
      object = object[element];
    }

    object[keyParts[keyParts.length - 1]] = value;

    setState({ ...state, checkoutData: checkoutData });
  }

  function setCompanies(companies) {
    const { checkoutData } = state;

    checkoutData.customerData.companies = companies;
    setState({ ...state, checkoutData: checkoutData });
  }

  function setAddress(address) {
    const { checkoutData } = state;

    checkoutData.customerData.address = address;
    setState({ ...state, checkoutData: checkoutData });
  }

  function setCompanyInvoiceDetails(companyDetails) {
    const { checkoutData } = state;

    checkoutData.customerData.companyInvoiceDetails.selectedCompany =
      companyDetails;
    setState({ ...state, checkoutData: checkoutData });
  }

  function setInvoiceEmail(email) {
    checkoutData.customerData.companyInvoiceDetails.companyInvoiceEmail = email;

    setState({ ...state, checkoutData: checkoutData });
  }

  function clearField(key, required = true) {
    const { textsMapping } = state;
    setData(key, "");
    updateFieldValidity(
      key,
      "text",
      !required,
      textsMapping.inputErrorMessageDefault
    );
  }

  function hasDigit(string) {
    const re = /\d/;
    return re.test(string);
  }

  function updateFieldValidity(key, inputType, valid, message) {
    const { textsMapping } = state;
    let newErrors = state.errors;

    if (inputType === "radio") {
      if (key === "customerData.invoiceToCompany" && getData(key) === false) {
        Object.keys(newErrors).forEach(
          (k) =>
            k.indexOf("customerData.companyInvoiceDetails.selectedCompany") ===
              0 && delete newErrors[k]
        );
      }

      return;
    }

    if (inputType === "email") {
      const trimmedEmail = String(getData(key)).trim();

      const re1 = /\S+@\S+\.\S+/;
      const hasNeededCharacters = re1.test(trimmedEmail);

      const re2 = /^[^.].*[^.]$/;
      const doesNotBeginOrEndWithDot = re2.test(trimmedEmail);

      if (hasNeededCharacters && doesNotBeginOrEndWithDot) {
        delete newErrors[key];
      } else {
        newErrors[key] = message;
      }

      return;
    }

    if (inputType === "select") {
      if (valid || String(getData(key)) !== "") {
        delete newErrors[key];
      } else {
        newErrors[key] = message;
      }

      return;
    }

    if (key === "customerData.accountDetails.password") {
      const re =
        /^((?=.*[A-Z])(?=.*[a-z])(?=.*\d)|(?=.*[a-z])(?=.*\d)(?=.*[\$\%\&])|(?=.*[A-Z])(?=.*\d)(?=.*[\$\%\&])|(?=.*[A-Z])(?=.*[a-z])(?=.*[\$\%\&])).{8,64}$/;

      if (re.test(String(getData(key)))) {
        delete newErrors[key];
      } else {
        newErrors[key] = message;
      }

      return;
    }

    if (key === "customerData.accountDetails.passwordRepeat") {
      if (getData(key) === getData("customerData.accountDetails.password")) {
        delete newErrors[key];
      } else {
        newErrors[key] = message;
      }

      return;
    }

    if (key === "customerData.personalDetails.insertion") {
      const value = String(getData(key));

      if (hasDigit(value)) {
        newErrors[key] = textsMapping.inputErrorMessageHasDigit;
      } else {
        delete newErrors[key];
      }

      return;
    }

    if (
      key === "customerData.personalDetails.firstName" ||
      key === "customerData.personalDetails.lastName"
    ) {
      const value = String(getData(key));

      if (!valid) {
        newErrors[key] = message;
      } else if (hasDigit(value)) {
        newErrors[key] = textsMapping.inputErrorMessageHasDigit;
      } else {
        delete newErrors[key];
      }

      return;
    }

    if (key === "customerData.personalDetails.dateOfBirth") {
      const re = /(0?[1-9]|[12][0-9]|3[01])[-](0?[1-9]|1[012])[-](\d{4})/;

      const dateString = getData(key);
      const dateParts = dateString.split("-");

      let day;
      let month;
      let year;

      let dateObject;

      if (dateParts.length == 3) {
        day = +dateParts[0];
        month = +dateParts[1];
        year = +dateParts[2];

        dateObject = new Date(year, month - 1, day);
      }

      if (
        re.test(dateString) &&
        dateObject &&
        !isNaN(dateObject) &&
        month == dateObject.getMonth() + 1 &&
        new Date() > dateObject
      ) {
        delete newErrors[key];
      } else {
        newErrors[key] = message;
      }

      return;
    }

    if (
      key ===
      "customerData.companyInvoiceDetails.selectedCompany.chamberOfCommerce"
    ) {
      const hasCorrectLength =
        getData(key) == null || getData(key).length <= 30;
      if (hasCorrectLength) {
        delete newErrors[key];
      } else {
        newErrors[key] = textsMapping.chamberOfCommerceValidationMessage;
      }

      return;
    }

    if (key === "customerData.companyInvoiceDetails.companyOrderReference") {
      const re = /^[^|<>?*%=&]*$/;
      const hasNoSpecialCharacters = re.test(String(getData(key)));

      if (!valid) {
        newErrors[key] = message;
      } else if (!hasNoSpecialCharacters) {
        newErrors[key] = textsMapping.inputErrorMessageOrderReference;
      } else {
        delete newErrors[key];
      }

      return;
    }

    if (valid) {
      delete newErrors[key];
    } else {
      newErrors[key] = message;
    }

    setState({ ...state, errors: newErrors });
  }

  function removeServerErrors() {
    setState({ ...state, serverErrors: [] });
  }

  function goToStep(action, stepNumber) {
    const {
      currentStep,
      checkoutData,
      lastStep,
      serverErrors,
      errors,
      accountAgreement,
      accountPrivacyAgreement,
    } = state;

    const currentStepEnum = getCurrentStepEnum(
      currentStep,
      checkHasModuleScheduler(),
      checkoutData.checkoutType
    );
    const isModuleStep = isStepModuleActive(currentStepEnum);
    const isPersonalDetailsStep = isStepPersonalActive(currentStepEnum);
    const isLastStep = currentStep === lastStep;
    const isFirstStep = currentStep === 0;

    if (action === "prev") {
      if (isFirstStep) {
        return;
      }

      setState({
        ...state,
        currentStep: currentStep - 1,
        checkProductSelectedCourse: true,
        customerDataValid: true,
      });
    }

    if (action === "next") {
      if (isLastStep) {
        return;
      }

      if (isModuleStep) {
        setState({
          ...state,
          invalidModuleItems: getInvalidModuleItems(),
        });

        if (!isAllModuleScheduleDataValid()) {
          setState({
            ...state,
            moduleScheduleDataValid: isAllModuleScheduleDataValid(),
          });

          return;
        }
      }

      if (isPersonalDetailsStep) {
        if (
          (Object.keys(errors).length !== 0 && errors.constructor === Object) ||
          !accountAgreement ||
          !accountPrivacyAgreement
        ) {
          setState({
            ...state,
            customerDataValid: false,
            invalidFormMessage: true,
          });
        } else {
          setState({
            ...state,
            customerDataValid: true,
          });
          handleCustomerData();
        }

        return;
      }

      if (serverErrors === undefined || serverErrors.length === 0) {
        setState({
          ...state,
          currentStep: currentStep + 1,
        });
      }
    }

    if (action === "jump") {
      setState({
        ...state,
        currentStep: stepNumber,
      });
    }
  }

  const {
    currentStep,
    checkoutData,
    errors,
    serverErrors,
    customerDataValid,
    accountAgreement,
    accountPrivacyAgreement,
    customerAddressNotFoundErrorMessage,
    companyAddressNotFoundErrorMessage,
    consentData,
    customerData,
    isOrderButtonLoading,
    invalidFormMessage,
    lastStep,
    cart,
    checkDiscountActive,
    textsMapping,
    invalidModuleItems,
  } = state;

  const { lineItems } = cart;
  const hasModuleScheduler = checkHasModuleScheduler();

  const currentStepEnum = getCurrentStepEnum(
    currentStep,
    hasModuleScheduler,
    checkoutData.checkoutType
  );
  const isModuleStepActive = isStepModuleActive(currentStepEnum);
  const isPersonalStepActive = isStepPersonalActive(currentStepEnum);
  const isSummaryStepActive = isStepSummaryActive(currentStepEnum);

  if (checkoutData === null) {
    return <div className="loading" />;
  }

  if (!lineItems.length) {
    return (
      <section className="checkout">
        <div className="cart-main__empty">
          <h1 className="cart-main__empty-title">
            {textsMapping.emptyCartTitle}
          </h1>
          <p className="cart-main__empty-text">
            {textsMapping.emptyCartText}
            <br />
            <br />
            <a href="/" className="button">
              {textsMapping.emptyCartHomeButtonText}
            </a>
          </p>
        </div>
      </section>
    );
  }

  return (
    <CheckoutContext.Provider
      value={{
        data: checkoutData,
        initialCheckoutData,
        errors,
        isModuleStepActive,
        isPersonalStepActive,
        isSummaryStepActive,
        textsMapping,
        currentStep,
        currentStepEnum,
        lastStep,
        hasModuleScheduler,
        isOrderButtonLoading,
        serverErrors,
        cartLineItems: lineItems,
        customerDataValid,
        accountAgreement,
        accountPrivacyAgreement,
        customerAddressNotFoundErrorMessage,
        cocEndpointUrl,
        companyAddressNotFoundErrorMessage,
        customerData,
        consentData,
        cart,
        checkDiscountActive,
        backToCartLink,
        invalidFormMessage,
        invalidModuleItems,
        goToStep,
        removeServerErrors,
        handleCourseClick,
        handleOrderConfirm,
        changeAccountAgreement,
        changeAccountPrivacyAgreement,
        getPersonalAddressDetails,
        getCompanyAddressDetails,
        trimInput,
        defaultCountryRadioButtonChanged,
        changeConsentAgreements,
        getData,
        setData,
        setCompanies,
        setAddress,
        setCompanyInvoiceDetails,
        setInvoiceEmail,
        updateFieldValidity,
        addressEndpointUrl,
      }}
    >
      <section className="checkout">
        {isCheckoutRegular(checkoutData.checkoutType) && <RegularCheckout />}

        {isCheckoutOffer(checkoutData.checkoutType) && <OfferCheckout />}
      </section>
    </CheckoutContext.Provider>
  );
}
